import React, { useCallback, useEffect, useRef, useState } from 'react';
import WheelCanvas from '../components/WheelCanvas';
import { WinnerImage } from '../components/WheelWinnerImage';

function decodeState(str) {
    try { return JSON.parse(decodeURIComponent(atob(str))); } catch { return null; }
}

export default function WheelOverlay() {
    const wheelRef = useRef(null);
    const bcRef = useRef(null);
    const [isSpinning, setIsSpinning] = useState(false);
    const [winner, setWinner] = useState(null);
    const [hasSpun, setHasSpun] = useState(false);
    const [winnerTotalCount, setWinnerTotalCount] = useState(0);

    // Parse initial state + session key + explicit WS URL from overlay URL
    const urlParams = new URLSearchParams(window.location.search);
    const urlState = (() => {
        const s = urlParams.get('s');
        return s ? decodeState(s) : null;
    })();
    const sessionKey = urlParams.get('key') || null;

    const filterSegments = (segs) => {
        if (!segs) return [];
        return segs.filter(s => {
            if (s.type === 'habbo') return !!s.habboName;
            if (s.type === 'furni') return s.furnis?.length > 0;
            if (s.type === 'custom') return !!s.text;
            return false;
        });
    };

    const [segments, setSegments] = useState(() => filterSegments(urlState?.segments));
    const [colors, setColors] = useState(() => urlState?.colors || ['#FFD700', '#FF6B6B']);
    const [fontValue, setFontValue] = useState(() => urlState?.fontValue || "'Jersey 10', sans-serif");
    const [spinDuration, setSpinDuration] = useState(() => urlState?.spinDuration || 5000);
    const [textSizeMul, setTextSizeMul] = useState(() => urlState?.textSizeMul ?? 1.35);
    const [textOrientation, setTextOrientation] = useState(() => urlState?.textOrientation || 'radial');

    // Keep a ref to segments so the BC handler can read the latest count
    const segmentsRef = useRef(segments);
    useEffect(() => { segmentsRef.current = segments; }, [segments]);

    // Shared message handler for both BC and WS
    const handleMessage = useCallback((data) => {
        if (data?.type === 'sync_idle') {
            wheelRef.current?.syncIdleRotation(data.rotation);
        } else if (data?.type === 'spin_sync') {
            // Shift the overlay's spin clock to stay frame-perfect with the main page
            wheelRef.current?.syncSpinClock(data.elapsed);
        } else if (data?.type === 'winner') {
            // Use the main page's authoritative winner rather than locally computed one
            setIsSpinning(false);
            setWinner(data.seg);
            setWinnerTotalCount(data.totalCount ?? segmentsRef.current.length);
        } else if (data?.type === 'dismiss') {
            setWinner(null);
            wheelRef.current?.clearWinner();
        } else if (data?.type === 'spin' && !wheelRef.current?.isSpinning()) {
            setWinner(null);
            setHasSpun(true);
            setIsSpinning(true);
            setWinnerTotalCount(segmentsRef.current.length);
            wheelRef.current?.spin({
                winnerIndex: data.winnerIndex,
                offsetFraction: data.offsetFraction,
                extraTurnCount: data.extraTurnCount,
                startRot: data.startRot,
                totalDelta: data.totalDelta, // authoritative value from main page — never recalculate locally
            });
        } else if (data?.type === 'state') {
            if (data.segments !== undefined) setSegments(filterSegments(data.segments));
            if (data.colors !== undefined) setColors(data.colors);
            if (data.fontValue !== undefined) setFontValue(data.fontValue);
            if (data.spinDuration !== undefined) setSpinDuration(data.spinDuration);
            if (data.textSizeMul !== undefined) setTextSizeMul(data.textSizeMul);
            if (data.textOrientation !== undefined) setTextOrientation(data.textOrientation);
        }
    }, []); // eslint-disable-line

    // SSE relay – reliable cross-browser / OBS (plain HTTP, no proxy WS issues)
    useEffect(() => {
        const apiUrl = process.env.REACT_APP_API_URL;
        if (!apiUrl || !sessionKey) return;
        const es = new EventSource(`${apiUrl}/wheel/events?key=${encodeURIComponent(sessionKey)}`);
        es.onmessage = (e) => { try { handleMessage(JSON.parse(e.data)); } catch {} };
        return () => es.close();
    }, [sessionKey, handleMessage]);

    // BroadcastChannel – same-browser fallback
    useEffect(() => {
        try {
            const bc = new BroadcastChannel('franklywheel_spin_v1');
            bcRef.current = bc;
            bc.onmessage = (e) => handleMessage(e.data);
        } catch {}
        return () => { try { bcRef.current?.close(); } catch {} };
    }, [handleMessage]);

    const handleWinnerPicked = useCallback(() => {
        setIsSpinning(false);
        // Winner state is set by the 'winner' broadcast from the main page, not local canvas computation
    }, []);

    const winnerLabel = winner ? (winner.label || winner.habboName || winner.text || 'Entry') : '';
    const oddsPercent = winnerTotalCount > 0 ? (100 / winnerTotalCount).toFixed(1) : '0';

    return (
        <div style={{ background: 'transparent', width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
            <style>{`
                html, body { background: transparent !important; margin: 0; padding: 0; overflow: hidden; }
            `}</style>

            {/* Square container that fills the smallest viewport dimension */}
            <div style={{ width: '100vmin', height: '100vmin', position: 'relative', flexShrink: 0 }}>
                <WheelCanvas
                    ref={wheelRef}
                    segments={segments}
                    colors={colors}
                    fontValue={fontValue}
                    spinDuration={spinDuration}
                    textSizeMultiplier={textSizeMul}
                    textOrientation={textOrientation}
                    onWinnerPicked={handleWinnerPicked}
                />

                {/* Winner overlay */}
                {winner && !isSpinning && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div
                            className="pointer-events-auto text-center bg-offwhite"
                            style={{
                                border: '0.7vmin solid #ffcc66',
                                boxShadow: '0 0 2.8vmin rgba(255,204,102,0.35), 0.8vmin 0.8vmin 0 rgba(0,0,0,0.55)',
                                padding: '2.4vmin 3.2vmin',
                                minWidth: '34vmin',
                                maxWidth: '82%',
                            }}
                        >
                            <p
                                className="font-['Jersey_10'] uppercase tracking-widest text-coal/50"
                                style={{ fontSize: '2.2vmin', marginBottom: '0.4vmin' }}
                            >
                                Winner!
                            </p>

                            <p
                                className="font-['Jersey_10'] uppercase text-coal leading-tight break-words"
                                style={{ fontSize: '6.4vmin' }}
                            >
                                {winnerLabel}
                            </p>

                            <WinnerImage seg={winner} />

                            <p
                                className="font-['Jersey_10'] text-coal/50 uppercase"
                                style={{ fontSize: '2.2vmin', marginTop: '0.8vmin' }}
                            >
                                1 in {winnerTotalCount} &nbsp;&middot;&nbsp; {oddsPercent}%
                            </p>
                        </div>
                    </div>
                )}

                {!hasSpun && segments.length >= 2 && (
                    <p style={{ position: 'absolute', bottom: 4, left: 0, right: 0, textAlign: 'center', color: 'rgba(255,255,255,0.6)', fontFamily: "'Jersey 10', sans-serif", fontSize: 14, textTransform: 'uppercase' }}>
                        Waiting for spin...
                    </p>
                )}

                {segments.length < 2 && (
                    <p style={{ position: 'absolute', bottom: 4, left: 0, right: 0, textAlign: 'center', color: 'rgba(255,255,255,0.6)', fontFamily: "'Jersey 10', sans-serif", fontSize: 14, textTransform: 'uppercase' }}>
                        Configure wheel on Frankly Origins
                    </p>
                )}
            </div>
        </div>
    );
}
